import { useMutation, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import type { DBNotification } from "../types";
import { HISTORY_KEY, INBOX_KEY } from "../queryKeys";
import { extractEventId } from "../lib/format";
import {
  breakfastEventsPrefix,
  pendingInvitesPrefix,
} from "@/features/breakfast/queryKeys";
import { trackedDaysPrefix } from "@/hooks/queries/useTrackedDays";
import { removeMyAttendeeRow } from "@/features/breakfast/mutations/removeAttendee";

/**
 * Bell-side handler for `EVENT_CANCELLED` notifications.
 *
 * Two distinct things happen under the hood when a host cancels an event
 * or removes an attendee (see `supabase/migrations/20260424120000_...sql`):
 *
 *   1. The `breakfast_events.status` flips to `'cancelled'`, OR the
 *      `breakfast_attendees.status` flips to `'removed'`. Neither causes
 *      a hard DELETE — the row keeps existing so the attendee's React
 *      Query cache can still resolve it and render the event as
 *      "Cancelled" on the calendar.
 *   2. A bell notification of type `EVENT_CANCELLED` is inserted for
 *      every affected attendee.
 *
 * The attendee's calendar view then stays in a "cancelled, acknowledge
 * me" state until they explicitly tap "Remove from calendar" here. That
 * click fires the user-scoped `remove_event_from_my_calendar` RPC which
 * is the ONLY place that hard-deletes their `breakfast_attendees` row
 * (and archives the matching bell notification). This is the same
 * ceremony Outlook uses and it fixes the 403 "You are not an attendee"
 * ghost-event bug: the event can no longer disappear out from under the
 * attendee — they have to acknowledge it first.
 */
export function useEventCancellationActions() {
  const queryClient = useQueryClient();

  const removeFromCalendar = useMutation({
    mutationFn: async ({ notif }: { notif: DBNotification }) => {
      const eventId = extractEventId(notif.action_url);
      if (!eventId) {
        throw new Error(
          "Could not locate the cancelled event. Please refresh and try again.",
        );
      }

      // Server-side: hard-deletes the attendee row for the caller and
      // archives every `EVENT_CANCELLED` notif pointing at this event.
      // Goes through the shared helper so that environments without
      // the migration applied transparently fall back to a direct
      // RLS-guarded DELETE.
      await removeMyAttendeeRow(eventId);

      // Belt-and-braces: the server-side archive update runs in the
      // same transaction as the DELETE, but if the caller races the
      // realtime subscription we may still have the optimistic row in
      // Inbox. Mark this notification read+archived directly so the
      // bell row disappears even if the fan-out query is slow.
      await supabase
        .from("notifications")
        .update({ is_read: true, is_archived: true })
        .eq("id", notif.id);

      return { eventId };
    },
    onMutate: async ({ notif }) => {
      await queryClient.cancelQueries({ queryKey: INBOX_KEY });
      await queryClient.cancelQueries({ queryKey: breakfastEventsPrefix });

      const prevInbox = queryClient.getQueryData<DBNotification[]>(INBOX_KEY);
      const prevEvents = queryClient.getQueriesData({
        queryKey: breakfastEventsPrefix,
      });

      queryClient.setQueryData<DBNotification[]>(INBOX_KEY, (old = []) =>
        old.filter((n) => n.id !== notif.id),
      );

      // Also evict the event from every cached calendar slice so the
      // "cancelled" card disappears from the grid the moment the user
      // taps "Remove from calendar" — no waiting for the server
      // round-trip or the subsequent refetch.
      const eventId = extractEventId(notif.action_url);
      if (eventId) {
        queryClient.setQueriesData(
          { queryKey: breakfastEventsPrefix },
          (old: unknown) => {
            if (!Array.isArray(old)) return old;
            return (old as Array<Record<string, unknown>>).filter(
              (ev) => ev.id !== eventId,
            );
          },
        );
      }

      return { prevInbox, prevEvents };
    },
    onSuccess: () => {
      // Bell + archive state update — no success toast; errors still toast.
      queryClient.invalidateQueries({ queryKey: HISTORY_KEY });
      queryClient.invalidateQueries({
        queryKey:    breakfastEventsPrefix,
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey:    pendingInvitesPrefix,
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey:    trackedDaysPrefix,
        refetchType: "all",
      });
    },
    onError: (err: Error, _vars, ctx) => {
      if (ctx?.prevInbox) queryClient.setQueryData(INBOX_KEY, ctx.prevInbox);
      if (ctx?.prevEvents) {
        for (const [qKey, data] of ctx.prevEvents) {
          queryClient.setQueryData(qKey as QueryKey, data);
        }
      }
      toast({
        title:       "Could not remove event",
        description: err.message,
        variant:     "destructive",
      });
    },
  });

  return { removeFromCalendar };
}
